/**
 * @auth 赖传峰
 * @date 2022/4/28
 * http://wiki.xunlei.cn/pages/viewpage.action?pageId=87264818
 */
import {
  type ReportTarget,
  type ReportType,
} from '~/components/home/resolve-source/reportTypes'
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ShareStatus } from '~/types/enum'

const category = 'web_share'
export type ShareSaveFrom = 'play_page' | 'share_page'
export type ShareSaveResult = 'success' | 'fail'

export type Systems = 'ios' | 'android' | 'other'
export type OpenAppButtons = 'to_appstore' | 'to_googleplay' | 'download_apk'

// 承接类型: normal-普通 |auto_jump-自动跳转 | auto_play-自动播放
export type UndertakeType = 'normal' | 'auto_jump' | 'auto_play'
// 展示样式: list_style-列表样式 | card_style-卡片样式
export type DisplayStyle = 'list_style' | 'card_style'
// 发起保存场景: share_file_list-分享文件列表 | preview-预览消费场景（包含视频预览、小说预览、压缩包在线解压）
export type SavingScene = 'share_file_list' | 'preview'

export interface ShareCommonParams {
  share_userid: string
  undertake_type: UndertakeType
  display_style: DisplayStyle
  saving_scene?: SavingScene
}

const actions = {
  /**
   * 分享承接-异常反馈页展示
   */
  web_share_abnormal_page_show(_: { error: ShareStatus }) { },
  /**
   * 分享承接-密码输入页展示
   */
  web_share_password_page_show() { },
  /**
   * 分享承接-密码校验结果
   * （用户输入密码，点击查看后上报）
   * result：密码校验结果，success-成功，fail-失败
   */
  web_share_password_request_result(_: { result: ShareSaveResult }) { },
  /**
   * 分享承接页面展示
   */
  web_share_page_show(_: ShareCommonParams) { },
  /**
   * 分享承接页面点击
   * button：按钮，save_all-保存全部文件（在分享页未选中点击保存时），save-保存（选中部分文件保存），more_save-操作浮层保存，crumbs-点击目录导航面包屑，open-打开文件，open_folder-打开文件夹，report-举报，download_guide-下载指南
   */
  web_share_page_click(_: ShareCommonParams & {
    button: 'save_all' | 'save' | 'more_save' | 'open' | 'open_folder' | 'crumbs' | 'open_app' | 'report' | 'download_guide'
  }) { },
  /**
   * 发起分享保存请求时上报
   * from：调起来源，play_page-预览播放界面，share_page-分享文件页面
   */
  web_share_save_request(_: ShareCommonParams & { from: ShareSaveFrom }) { },
  /**
   * 分享保存成功时上报
   * from：调起来源，play_page-预览播放界面，share_page-分享文件页面
   */
  web_share_save_success(_: ShareCommonParams & { from: ShareSaveFrom }) { },
  /**
   * 分享保存失败时上报
   * from：调起来源，play_page-预览播放界面，share_page-分享文件页面
   * error：服务端返回错误key，异常页面展示时上报
   */
  web_share_save_fail(_: ShareCommonParams & { from: ShareSaveFrom, error: string }) { },
  /**
   * 分享保存结果页展示
   * from：调起来源，share_page-分享文件页面
   * save_result：保存结果，success-保存成功，fail-保存失败
   */
  web_share_save_result_page_show(_: ShareCommonParams & { save_result: ShareSaveResult, from: 'share_page' }) { },
  /**
   * 分享保存结果页点击
   * from：调起来源，share_page-分享文件页面
   * save_result：保存结果，success-保存成功，fail-保存失败
   * button：按钮，open_inapp-APP内查看，view_in_folder-点击存储目标名称，return_retry-返回重试
   */
  web_share_save_result_page_click(_: ShareCommonParams & {
    save_result: ShareSaveResult,
    button: 'open_inapp' | 'view_in_folder' | 'return_retry',
    from: 'share_page'
  }) { },
  /** 打开 App 引导浮窗展示 */
  web_share_open_app_pop_show(_: { system: Systems }) {},
  /** 打开 App 引导浮窗点击 */
  web_share_open_app_pop_click(_: { system: Systems; button: OpenAppButtons | 'open_directly' }) {},
  /** 保存文件引导浮窗展示 */
  web_share_save_file_pop_show(_: { system: Systems }) {},
  /** 保存文件引导浮窗点击 */
  web_share_save_file_pop_click(_: { system: Systems; button: OpenAppButtons | 'continue_to_save' | 'save_on_web' }) {},
  /** 分享预览结束浮层展示 */
  preview_end_floating_show(_: ShareCommonParams) {},
  /** 分享预览结束浮层点击 */
  preview_end_floating_click(_: ShareCommonParams & { button: 'save_now' | 'rewatch' | 'close' | 'save_all' }) {},
  /**
   * 分享代码跳转确认浮层展示
   * share_id：当前分享shareid
   * share_userid：当前分享创建者 UID
   * undertake_type：承接类型 normal-普通 auto_jump-自动跳转 auto_play-自动播放
   * display_style：展示样式 list_style-列表样式 card_style-卡片样式
   * code_text：当前分享代码文案
   */
  share_code_jump_confirmation_show(_: { code_text: string } & ShareCommonParams) {},
  /**
   * 分享代码跳转确认浮层点击
   * share_id：当前分享shareid
   * share_userid：当前分享创建者 UID
   * undertake_type：承接类型 auto_jump-自动跳转 auto_play-自动播放
   * display_style：展示样式 list_style-列表样式 card_style-卡片样式
   * code_text：当前分享代码文案
   * button：按钮 view-立即查看 view_without_prompting-不再提示直接查看 close - 关闭
   */
  share_code_jump_confirmation_click(_: Parameters<typeof this.share_code_jump_confirmation_show>[0] & {
    button: 'view' | 'view_without_prompting' | 'close'
  }) {},
  /**
   * 分享页举报弹窗展示
   * share_id 分享 Shareid
   * share_userid 分享创建者 UID
   */
  share_report_pop_show(_: {
    share_id: string
    share_userid: string
  }) {},
  /**
   * 分享页发起举报请求
   * share_id 分享 share id
   * share_userid 分享创建者 UID
   * report_target 举报目标 whole_sharing-整个分享，current_folder-当前文件夹，partial_files-部分文件
   * report_reason 举报原因
   */
  share_report_request(_: {
    report_target: ReportTarget,
    report_reason: ReportType
  } & Parameters<typeof this.share_report_pop_show>[number]) {},

  /**
   * 分享页发起举报结果
   * result 结果
   * share_id 分享 share id
   * share_userid 分享创建者 UID
   * report_target 举报目标
   * report_reason 举报原因
   */
  share_report_result(_: {
    result: 'success' | 'fail'
  } & Parameters<typeof this.share_report_request>[number]) {},

  /**
   * 分享页面下载引导点击
   * button：点击位置，download_pc-下载PC版，download_ms_pc-下载MS PC版，web_client-访问网页版，download_mac-下载Mac版
   */
  web_share_page_download_guide_click(_: { button: 'download_pc' | 'install_app' | 'download_mac' }) {},
}

const getShareIdFromPathname = () => window.location.pathname.split('/')[2]

export default new Proxy(actions, {
  get(target, key: keyof typeof actions) {
    return (data = {} as Parameters<typeof target[typeof key]>[number]) => window.stat(
      category,
      key,
      { share_id: getShareIdFromPathname(), ...data },
    )
  },
})
